import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  DisplayText,
  IllusLocation,
  Image,
  Modal,
  theme,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useCart } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

// Warning for when the customer attempts to edit an address that has a order associated with it.
// Since the address is linked to the order object in gravy, changing the address requires clearing the bag.
export const ActiveBagWarning = ({
  visible,
  onClose,
  onConfirm,
}: ActiveBagWarningProps) => {
  const { track } = useTelemetry();

  React.useEffect(() => {
    if (!visible) return;
    track('addresses.active_bag');
  }, [track, visible]);

  return (
    <Modal
      animationType="fade"
      size="medium"
      visible={visible}
      onRequestClose={onClose}
    >
      <View
        style={styles.container}
        testID="account.addresses.active-bag-warning.container"
      >
        <View style={styles.row}>
          <DesktopIllustration />
          <WarningInfo />
        </View>
        <WarningControls onClose={onClose} onConfirm={onConfirm} />
      </View>
    </Modal>
  );
};

// Intercepts an attempt to edit an address.
// Will only allow it if it's not the address for the current bag.
// Returns callbacks to be used in the `<ActiveBagWarning />` modal.
export const useActiveBagWarning = (
  proceedToEdit: (addressId: string) => void,
) => {
  // ─────────── State ───────────
  const { cart, isCartEmpty } = useCart();
  const currentBagAddressId =
    !isCartEmpty && cart?.deliveryOrderDetail?.address?.id;
  const [activeBagWarning, setActiveBagWarning] = React.useState('');

  // ────── Modal Callbacks ──────
  const closeActiveBagWarning = React.useCallback(() => {
    setActiveBagWarning('');
  }, [setActiveBagWarning]);
  const confirmEditAddress = React.useCallback(() => {
    proceedToEdit(activeBagWarning);
    closeActiveBagWarning();
  }, [activeBagWarning, proceedToEdit, closeActiveBagWarning]);

  // ───── Actual Edit Request ─────
  const onEdit = React.useCallback(
    (addressId: string) => {
      if (addressId === currentBagAddressId) {
        setActiveBagWarning(addressId);

        return;
      }

      proceedToEdit(addressId);
    },
    [currentBagAddressId, proceedToEdit],
  );

  // ────── Component Usage ──────
  return {
    activeBagWarning,
    closeActiveBagWarning,
    confirmEditAddress,
    onEdit,
  };
};

const DesktopIllustration = () => {
  const { currentBreakpoint } = useBreakpointHelpers();

  if (currentBreakpoint.isMobile) return null;

  return (
    <Image
      source={IllusLocation}
      style={styles.illustration}
      accessibilityIgnoresInvertColors
    />
  );
};

const WarningInfo = () => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useBreakpointHelpers();
  const { isMobile } = currentBreakpoint;
  const textStyle = isMobile ? styles.mobileSubtitle : styles.subtitle;

  return (
    <View style={styles.info}>
      <DisplayText size={3}>
        {t('account.addresses.active-bag-warning.title')}
      </DisplayText>
      <BodyText size={3} style={textStyle}>
        {t('account.addresses.active-bag-warning.subtitle')}
      </BodyText>
    </View>
  );
};

const WarningControls = ({
  onClose,
  onConfirm,
}: Pick<ActiveBagWarningProps, 'onClose' | 'onConfirm'>) => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.row}>
      <Button
        palette="secondary"
        size="large"
        width="47.5%"
        onPress={onClose}
        testID="account.addresses.active-bag-warning.close"
        accessibilityLabel={t('account.addresses.active-bag-warning.cta')}
        accessibilityHint={t('account.addresses.active-bag-warning.cta')}
        accessibilityRole="button"
      >
        {t('general.close')}
      </Button>
      <Button
        size="large"
        width="47.5%"
        onPress={onConfirm}
        testID="account.addresses.active-bag-warning.confirm"
        accessibilityLabel={t('account.addresses.active-bag-warning.cta')}
        accessibilityHint={t('account.addresses.active-bag-warning.cta')}
        accessibilityRole="button"
      >
        {t('account.addresses.active-bag-warning.cta')}
      </Button>
    </View>
  );
};

type ActiveBagWarningProps = {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
};

const ILLUSTRATION_SIZE = 220;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: theme.spacing.large,
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    flex: 1,
  },
  row: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  subtitle: {
    marginTop: theme.spacing.small,
  },
  mobileSubtitle: {
    marginTop: theme.spacing.medium,
    marginBottom: theme.spacing.xlarge - theme.spacing.small,
  },
  illustration: {
    marginRight: theme.spacing.xsmall,
    marginBottom: theme.spacing.xsmall,
    width: ILLUSTRATION_SIZE,
    height: ILLUSTRATION_SIZE,
  },
});
